import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Image,
  Divider,
  List,
  ListItem,
  ListIcon,
  UnorderedList,
  Link,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaMicrochip,
  FaArrowLeft,
  FaCircle,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const TermsOfService = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Box>
      <Helmet>
        <title>Terms of Service | SRS</title>
        <meta name="description" content="Terms of Use governing access to the SRS platform and services operated by Empirisch Tech GmbH." />
        <link rel="canonical" href="https://srs.numericalai.net/terms" />
        <meta property="og:title" content="Terms of Service | SRS" />
        <meta property="og:description" content="Terms of Use governing access to the SRS platform and services operated by Empirisch Tech GmbH." />
        <meta property="og:url" content="https://srs.numericalai.net/terms" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | SRS" />
        <meta name="twitter:description" content="Terms of Use governing access to the SRS platform and services operated by Empirisch Tech GmbH." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button 
                leftIcon={<FaArrowLeft />}
                variant="ghost" 
                as={RouterLink} 
                to="/"
              >
                Back to Home
              </Button>
              {user ? (
                <Button 
                leftIcon={<FaCloud />}
                colorScheme="brand"
                size="sm"
                variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                  }}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Terms Content */}
      <Container maxW="container.lg" py={12}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Heading as="h1" size="2xl" color="gray.800">
              Terms of Use (Nutzungsbedingungen)
            </Heading>
            <Text fontSize="lg" color="gray.600" fontWeight="bold">
              Effective Date: 05.01.2026
            </Text>
          </VStack>

          <VStack spacing={6} align="stretch">
            <Box>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Welcome to SRS, a cloud-based simulation platform ("Platform") operated by Empirisch Tech GmbH, Rennweg 88, 1030 Vienna, Austria ("we", "us", "our").
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                These Terms of Use ("Terms") govern your access and use of https://srs.numericalai.net/ ("Site") and the related online services, including uploading simulation data, running cloud simulations, and receiving AI-assisted guidance ("Services").
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
                By creating an account or using the Platform, you agree to these Terms.
              </Text>
            </Box>

            <Divider />

            {/* Section 1 */}
            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                1. Operator Information (Impressum Requirement)
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={2}>
                <Text as="span" fontWeight="bold">Empirisch Tech GmbH</Text>, legally represented by the managing director Dr. Wasif Masood
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}>
                Rennweg 88, 1030 Vienna, Austria
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}>
                Legal Form: Limited Liability (Ltd./GmbH)
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}> Object of the company: IT Consulting</Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}> Corporate register number: FN 566531 i</Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}> Corporate register court: Handelsgericht Wien</Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}> Member of WKO (Wirtschaftskammer Österreich)</Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}> Awarding country: Austria</Text>
              <Text color="gray.600" lineHeight="1.8" mb={2}>
                Responsible for content according to § 55 RStV: Dr. Wasif Masood
              </Text>


              <Link 
                color="brand.500" 
                fontWeight="medium"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate('/settings')}
                cursor="pointer"
              >
                Contact Form.
              </Link>
            </Box>
{/* Section 2 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    2. Eligibility
  </Heading>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    To use the Platform, you must meet all of the following requirements:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Be at least 18 years of age, or the age of legal majority in your jurisdiction</ListItem>
    <ListItem>Have full legal capacity to enter into binding agreements under applicable law</ListItem>
    <ListItem>If acting on behalf of a company, organization, or other legal entity, have the authority to bind that entity to these Terms</ListItem>
    <ListItem>Not be located in, or a resident of, any country subject to export restrictions or sanctions (including but not limited to: Cuba, Iran, North Korea, Syria, and the Crimea region)</ListItem>
    <ListItem>Not be listed on any government prohibited or restricted parties list</ListItem>
    <ListItem>Have access to the internet and equipment necessary to use the Platform</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    By creating an account, you represent and warrant that you meet all eligibility requirements. 
    We reserve the right to verify your eligibility at any time and to refuse service, 
    terminate accounts, or cancel orders if eligibility requirements are not met.
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    The Platform is intended for professional, academic, and research use. Users should have 
    appropriate technical knowledge to interpret and apply simulation results responsibly.
  </Text>
</Box>

{/* Section 3 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    3. Account Registration and Security
  </Heading>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="bold">3.1 Registration Requirements.</Text> To access the Platform's 
    services, you must create an account by providing accurate, current, and complete information 
    including your name, email address, and any other required details. You agree to update this 
    information promptly if it changes.
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="bold">3.2 Account Security.</Text> You are responsible for:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Maintaining the confidentiality of your login credentials</ListItem>
    <ListItem>All activities that occur under your account, whether authorized by you or not</ListItem>
    <ListItem>Notifying us immediately at support if you suspect unauthorized access or security breaches</ListItem>
    <ListItem>Not sharing your account or credentials with any third party</ListItem>
    <ListItem>Using strong, unique passwords and enabling additional security measures when available</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="bold">3.3 One Account Policy.</Text> Each individual or entity may 
    maintain only one active account. Creating multiple accounts to circumvent usage limits, bans, 
    or to abuse promotional offers is strictly prohibited and grounds for immediate termination.
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="bold">3.4 Account Suspension and Termination.</Text> We may, at our 
    sole discretion and without prior notice, suspend or permanently terminate your account if:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You violate these Terms of Service or any applicable laws</ListItem>
    <ListItem>You provide false, misleading, or fraudulent information</ListItem>
    <ListItem>You engage in abusive, harmful, or unauthorized use of the Platform</ListItem>
    <ListItem>Your account is used for illegal activities or export-controlled purposes</ListItem>
    <ListItem>We are required to do so by law or regulatory authority</ListItem>
    <ListItem>Your account remains inactive for an extended period (typically 24 months)</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8">
    <Text as="span" fontWeight="bold">3.5 Effect of Termination.</Text> Upon account termination, 
    your right to access the Platform ceases immediately. Any unused credits or prepaid services 
    are non-refundable unless otherwise required by law. We may retain certain data as required 
    for legal, tax, or audit purposes as described in our Privacy Policy.
  </Text>
</Box>
{/* Section 4 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    4. Scope of Services
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    4.1 Core Simulation Services
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform provides cloud-based computational fluid dynamics (CFD) simulation services, including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Scale-Resolving Simulations (SRS) of turbulent flows executed on GPUs, powered by the PyFR engine</ListItem>
    <ListItem>Support for 2D and 3D simulations on unstructured mixed-element meshes</ListItem>
    <ListItem>File upload (ZIP archives containing pyfr.ini and mesh files)</ListItem>
    <ListItem>Job queuing, execution, monitoring, and result download</ListItem>
    <ListItem>Estimated simulation runtime based on the average time per step of the simulation and is subject to change at run time</ListItem>
    <ListItem>Secure cloud storage of simulation inputs and outputs</ListItem>
    <ListItem>User dashboard for managing simulations, credits, and settings</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    4.2 Code Validation Pipeline
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Before simulation execution, all submitted code undergoes a mandatory validation process:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">Python Syntax Check:</Text> Validates code structure and detects potential security threats (malware scanning). Required for all users.</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">System Configuration Check:</Text> Verifies simulation parameters and system compatibility. Required for all users.</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Grid Resolution Analysis:</Text> Evaluates mesh quality and resolution settings. Optional for Premium users.</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">AI-Assisted Review:</Text> Provides optimization recommendations and error detection. Optional for Premium users.</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    4.3 User Tiers
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">€1 Starter Users:</Text> Receive two (2) hours of compute time with basic Python validation only, activated via a one-time €1 account fee.</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Premium Users:</Text> Access to advanced AI-assisted validation, debugging tools, priority queue, and additional compute credits.</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Enterprise Users:</Text> Unlimited compute time on desired GPUs, dedicated queue, unlimited parallel simulations, and access to Structural, Multiphysics, and Molecular Dynamics capabilities.</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    4.4 Service Availability
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We strive to maintain high availability but do not guarantee uninterrupted service. The Platform may be 
    temporarily unavailable due to maintenance, updates, or circumstances beyond our control. Simulation 
    processing times depend on queue length, job complexity, and computational resources. The estimated simulation runtime is based on the average time per step of the simulation and is subject to change at run time. This is an estimate and is not a guarantee of the actual runtime.
  </Text>
</Box>

{/* Section 5 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    5. AI-Assisted Features and Limitations
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    5.1 Nature of AI Services
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform integrates artificial intelligence to assist users with simulation setup, validation, 
    and optimization. AI-assisted features include:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Automated code review and syntax validation</ListItem>
    <ListItem>Security scanning and malware detection</ListItem>
    <ListItem>Grid quality analysis and mesh recommendations</ListItem>
    <ListItem>Performance optimization suggestions</ListItem>
    <ListItem>Error detection and diagnostic hints</ListItem>
    <ListItem>Computational model improvement recommendations</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    5.2 No Professional Advice
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    AI-generated outputs, including tips, analyses, suggestions, and recommendations:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Are provided for informational and educational purposes only</ListItem>
    <ListItem>Are automatically generated without human review or verification</ListItem>
    <ListItem>Do not constitute professional engineering, scientific, legal, or technical advice</ListItem>
    <ListItem>Must not be used as the sole basis for safety-critical, structural, regulatory, or life-affecting decisions</ListItem>
    <ListItem>Require independent validation and verification by qualified professionals</ListItem>
    <ListItem>May be inaccurate, incomplete, outdated, or unsuitable for your specific use case</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    5.3 No Accuracy or Fitness Guarantee
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    AI outputs are probabilistic in nature. We expressly disclaim any warranty or guarantee regarding:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Accuracy, correctness, or completeness of AI suggestions</ListItem>
    <ListItem>Reliability or consistency of AI-generated analyses</ListItem>
    <ListItem>Fitness for any particular purpose or application</ListItem>
    <ListItem>Suitability for regulatory compliance or certification purposes</ListItem>
    <ListItem>Absence of errors, bugs, or misleading recommendations</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    5.4 User Responsibility
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You acknowledge and agree that you are solely responsible for:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Independently verifying and validating all AI outputs before reliance</ListItem>
    <ListItem>Ensuring simulation correctness, accuracy, and applicability to your specific use case</ListItem>
    <ListItem>Making all engineering, technical, scientific, and business decisions</ListItem>
    <ListItem>Evaluating and interpreting AI suggestions with appropriate professional judgment</ListItem>
    <ListItem>Obtaining independent expert review for safety-critical or high-stakes applications</ListItem>
    <ListItem>Any consequences arising from your use or misuse of AI-generated content</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    5.5 Simulation Results Disclaimer
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Simulation results generated by the Platform are computational approximations based on the inputs, 
    parameters, and models you provide. Results may differ from real-world physical behavior due to 
    modeling assumptions, numerical approximations, mesh resolution, and other factors. You must 
    independently validate all simulation results against experimental data or established benchmarks 
    before applying them to real-world engineering decisions, product designs, or safety assessments.
  </Text>
</Box>
{/* Section 6 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    6. Compliance with EU AI Regulations
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    6.1 AI Act Classification
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform utilizes general-purpose AI components for code analysis, validation, and optimization 
    suggestions. These AI systems are designed as assistive tools and are not intended for autonomous 
    decision-making in regulated or safety-critical applications.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    6.2 Prohibited High-Risk Applications
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You expressly agree not to use AI-generated outputs from this Platform for, or in connection with, 
    any of the following high-risk applications as defined under the EU AI Act and related regulations:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>
      <Text as="span" fontWeight="semibold">Critical infrastructure:</Text> Energy, water supply, transportation, 
      telecommunications, or other essential services
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Safety components:</Text> Structural engineering, load-bearing systems, 
      pressure vessels, or any components where failure could cause injury or death
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Medical and healthcare:</Text> Medical devices, diagnostic tools, 
      treatment planning, pharmaceutical development, or life sciences applications
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Aviation and aerospace:</Text> Aircraft design, flight systems, 
      space vehicles, or any aviation-certified components
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Autonomous systems:</Text> Self-driving vehicles, drones, robotics, 
      or any systems operating without human oversight
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Nuclear applications:</Text> Nuclear power, radioactive materials 
      handling, or related safety systems
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Defense and weapons:</Text> Military applications, weapons systems, 
      or dual-use technologies subject to export controls
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Regulatory submissions:</Text> Certification processes, regulatory 
      compliance documentation, or official technical assessments without independent expert verification
    </ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    6.3 Transparency and Human Oversight
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    In accordance with EU AI Act transparency requirements:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>All AI-generated outputs are clearly labeled as such within the Platform</ListItem>
    <ListItem>AI suggestions require human review and approval before implementation</ListItem>
    <ListItem>You must maintain human oversight when using any AI-assisted features</ListItem>
    <ListItem>You must not represent AI outputs as human-generated professional advice</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    6.4 User Notification Obligations
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You agree to notify us immediately via the contact form if:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You intend to integrate AI outputs into any regulated environment or product</ListItem>
    <ListItem>You discover that AI outputs have been used in a prohibited application</ListItem>
    <ListItem>You become aware of any harm or potential harm resulting from AI outputs</ListItem>
    <ListItem>Third parties request access to AI-generated content for regulatory purposes</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    6.5 Consequences of Non-Compliance
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Violation of these AI usage restrictions may result in immediate account suspension or termination, 
    reporting to relevant regulatory authorities, and you assuming full liability for any resulting 
    damages, fines, or legal consequences. We reserve the right to cooperate with regulatory 
    investigations related to misuse of AI features.
  </Text>
</Box>

{/* Section 7 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    7. User Content
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.1 Definition of User Content
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    "User Content" includes all materials you upload, submit, or create on the Platform, including but 
    not limited to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Simulation configuration files (pyfr.ini and related input files)</ListItem>
    <ListItem>ZIP archives containing simulation data, meshes, and input parameters</ListItem>
    <ListItem>Job names, descriptions, and metadata</ListItem>
    <ListItem>Simulation results, outputs, and generated data files</ListItem>
    <ListItem>Code submitted for validation and analysis</ListItem>
    <ListItem>Communications sent through the Platform (support requests, feedback)</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.2 Ownership and Intellectual Property
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You retain all ownership rights, intellectual property rights, and proprietary interests in your 
    User Content. We do not claim any ownership over your simulations, models, data, or results. 
    Your trade secrets, proprietary algorithms, and confidential information remain yours.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.3 License Grant to Process
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    By uploading User Content, you grant us a limited, non-exclusive, worldwide, royalty-free license to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Store, host, and backup User Content on our servers and cloud infrastructure</ListItem>
    <ListItem>Process and execute simulations according to your instructions</ListItem>
    <ListItem>Analyze code through our validation pipeline (Python check, system check, grid check)</ListItem>
    <ListItem>Apply AI-assisted analysis for error detection, optimization, and suggestions</ListItem>
    <ListItem>Generate and deliver simulation results to you</ListItem>
    <ListItem>Create anonymized, aggregated statistics for service improvement (no individual data shared)</ListItem>    
    <ListItem>Use parts of simulation data to improve and train our AI models (only when you have explicitly consented in your account settings). See section 3.3 of our <Link as={RouterLink} fontWeight="medium" to="/privacy" color="brand.500">privacy policy</Link> for details.
    </ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    This license is limited to the purposes of providing the Platform services and terminates upon 
    deletion of your account or User Content, except as required for legal compliance.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.4 AI Processing Disclosure
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Your User Content may be processed by AI systems for:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Security scanning and malware detection</ListItem>
    <ListItem>Syntax validation and error identification</ListItem>
    <ListItem>Grid quality and mesh resolution analysis</ListItem>
    <ListItem>Performance optimization recommendations</ListItem>
    <ListItem>Computational model improvement suggestions</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.5 Data Retention and Deletion
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Simulation files and results are retained while your account is active</ListItem>
    <ListItem>You may delete individual jobs and their associated data at any time via the Jobs page</ListItem>
    <ListItem>Upon account deletion, all personal, usage, and simulation data is permanently deleted</ListItem>
    <ListItem>Billing records are retained for seven (7) years after the last purchase as required by tax and accounting laws</ListItem>
    <ListItem>Anonymized, aggregated analytics data may be retained indefinitely</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.6 Prohibited Content
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You represent and warrant that your User Content does not:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Infringe any third-party intellectual property rights (patents, copyrights, trade secrets)</ListItem>
    <ListItem>Contain malicious code, viruses, malware, or security exploits</ListItem>
    <ListItem>Include unlawful, harmful, threatening, or harassing material</ListItem>
    <ListItem>Contain personal data of third parties without proper consent or legal basis under GDPR</ListItem>
    <ListItem>Violate export control laws, sanctions, or regulations (ITAR, EAR, EU dual-use)</ListItem>
    <ListItem>Relate to weapons development, nuclear technology, or controlled military applications</ListItem>
    <ListItem>Constitute classified, restricted, or government-controlled information</ListItem>
    <ListItem>Violate any applicable local, national, or international laws</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.7 Content Moderation and Removal
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    In compliance with the EU Digital Services Act (DSA) and our security obligations:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>We may scan uploaded content for security threats and policy violations</ListItem>
    <ListItem>We reserve the right to reject, block, or remove content that violates these Terms</ListItem>
    <ListItem>Content flagged as malware during validation will be automatically rejected</ListItem>
    <ListItem>We may suspend accounts pending investigation of suspected violations</ListItem>
    <ListItem>We will respond to valid legal requests and court orders regarding User Content</ListItem>
    <ListItem>We provide a mechanism for reporting illegal content through our contact form</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    7.8 Your Responsibility and Indemnification
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    You are solely responsible for your User Content and any consequences of uploading or sharing it. 
    You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from 
    your User Content, including claims of intellectual property infringement, export control violations, 
    or data protection breaches caused by your submissions.
  </Text>
</Box>
{/* Section 8 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    8. Acceptable Use Policy
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.1 General Conduct
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You agree to use the Platform only for lawful purposes and in accordance with these Terms. 
    Your use must comply with all applicable local, national, EU, and international laws and regulations.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.2 Prohibited Activities
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You expressly agree NOT to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>
      <Text as="span" fontWeight="semibold">Legal violations:</Text> Use the Platform in violation of EU, 
      German, Austrian, or any other applicable laws, including data protection (GDPR), export control, 
      and sanctions regulations
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Security interference:</Text> Attempt to probe, scan, test, 
      or breach the security or authentication measures of the Platform or its infrastructure
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">System disruption:</Text> Interfere with, disrupt, or create 
      an undue burden on the Platform's servers, networks, or connected systems
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Malicious content:</Text> Upload, transmit, or execute malware, 
      viruses, trojans, ransomware, or any code designed to harm, exploit, or gain unauthorized access
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Resource abuse:</Text> Run simulations designed to consume 
      excessive computational resources, exploit system vulnerabilities, or deny service to other users
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Reverse engineering:</Text> Decompile, disassemble, reverse 
      engineer, or attempt to derive the source code, algorithms, or architecture of the Platform
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Circumvention:</Text> Bypass, disable, or interfere with 
      security features, usage limits, credit systems, or access controls
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Unauthorized access:</Text> Access accounts, data, or systems 
      belonging to other users without authorization
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">AI misuse:</Text> Use AI features for high-risk, regulated, 
      or prohibited applications as specified in Section 6 (EU AI Act Compliance)
    </ListItem>
    <ListItem>
      <Text as="span" fontWeight="semibold">Impersonation:</Text> Misrepresent your identity, affiliation, 
      or the origin of content submitted to the Platform
    </ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.3 Fair Use and Rate Limits
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You agree to use the Platform fairly and not to monopolize shared computational resources</ListItem>
    <ListItem>We may implement rate limits, queue prioritization, or usage caps to ensure equitable access</ListItem>
    <ListItem>Automated or scripted access (bots, scrapers, automated job submission) is strictly prohibited </ListItem>
    <ListItem>API access, if provided, must be used in accordance with any published API terms and rate limits</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.4 Account Integrity
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You may not share, sell, transfer, or sublicense your account or credentials to third parties</ListItem>
    <ListItem>You may not create multiple accounts to circumvent usage limits, bans, or promotional restrictions</ListItem>
    <ListItem>You may not use the Platform for competitive intelligence, benchmarking against competing 
    services, or to develop competing products without our consent</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.5 Export Control Compliance
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You acknowledge that simulation software and computational fluid dynamics tools may be subject to 
    export control regulations. You agree not to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Use the Platform for purposes prohibited under US Export Administration Regulations (EAR), 
    EU dual-use regulations, or other applicable export laws</ListItem>
    <ListItem>Submit content related to weapons development, nuclear applications, or other controlled technologies</ListItem>
    <ListItem>Access the Platform from, or transfer results to, sanctioned countries or prohibited parties</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    8.6 Enforcement
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    We reserve the right to investigate suspected violations and take appropriate action, including 
    warning, temporary suspension, permanent termination, content removal, and reporting to law 
    enforcement or regulatory authorities. We may cooperate with legal requests and preserve evidence 
    of violations. Violations may result in forfeiture of unused credits without refund.
  </Text>
</Box>

{/* Section 9 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    9. Simulation Output and Results
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.1 Ownership of Output
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Simulation outputs, results, data files, visualizations, and reports generated from your User Content 
    ("Simulation Output") belong to you, subject to the following conditions:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Underlying third-party tool licenses (e.g., PyFR, released under a 3-Clause BSD License)</ListItem>
    <ListItem>Any applicable open-source licenses governing simulation tools</ListItem>
    <ListItem>Technical limitations of the Platform</ListItem>
    <ListItem>These Terms of Service</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.2 Downloading and Access
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Simulation Output is available for download via the Jobs page while your account is active</ListItem>
    <ListItem>Results are provided as ZIP archives containing output data files</ListItem>
    <ListItem>Download links are time-limited and authenticated for security purposes</ListItem>
    <ListItem>We do not guarantee indefinite storage of Simulation Output; you are responsible for 
    downloading and backing up your results</ListItem>
    <ListItem>Results from deleted jobs cannot be recovered</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.3 No Warranty or Guarantee of Accuracy
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Simulation Output, including any AI-assisted analysis or suggestions, is provided "AS IS" without 
    warranty of any kind. We expressly disclaim all warranties regarding Simulation Output, including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Accuracy, correctness, or precision of numerical results</ListItem>
    <ListItem>Completeness or comprehensiveness of output data</ListItem>
    <ListItem>Fitness for any particular purpose, application, or use case</ListItem>
    <ListItem>Conformance with experimental data, theoretical predictions, or real-world behavior</ListItem>
    <ListItem>Suitability for engineering design, safety analysis, or regulatory submissions</ListItem>
    <ListItem>Absence of numerical errors, convergence issues, or computational artifacts</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.4 Limitations of Computational Simulation
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You acknowledge that computational simulations are mathematical approximations subject to inherent 
    limitations including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Modeling assumptions and simplifications</ListItem>
    <ListItem>Numerical discretization and truncation errors</ListItem>
    <ListItem>Mesh resolution and quality effects</ListItem>
    <ListItem>Boundary condition approximations</ListItem>
    <ListItem>Turbulence model limitations</ListItem>
    <ListItem>Hardware floating-point precision</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.5 Verification and Validation Responsibility
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    YOU ARE SOLELY RESPONSIBLE FOR:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Independently verifying and validating all Simulation Output before any use or reliance</ListItem>
    <ListItem>Comparing results against analytical solutions, experimental data, or established benchmarks</ListItem>
    <ListItem>Conducting mesh independence studies and convergence analyses</ListItem>
    <ListItem>Obtaining professional engineering review for any design or safety-related applications</ListItem>
    <ListItem>Ensuring compliance with applicable industry standards, codes, and regulations</ListItem>
    <ListItem>Making all final engineering, scientific, and business decisions based on your own judgment</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.6 Publication and Attribution
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    If you publish, present, or share Simulation Output in academic papers, reports, or public forums:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You may acknowledge the use of SRS and the PyFR engine if appropriate</ListItem>
    <ListItem>You remain solely responsible for the accuracy and interpretation of published results</ListItem>
    <ListItem>Publication does not imply endorsement or verification by SRS</ListItem>
    <ListItem>You must comply with any citation requirements of underlying open-source tools</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    9.7 Liability for Downstream Use
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    You assume all risk and liability for any downstream use of Simulation Output, including use in 
    product design, engineering analysis, regulatory submissions, academic publications, or commercial 
    applications. We are not liable for any damages, losses, injuries, or consequences arising from 
    your use of, reliance on, or decisions based on Simulation Output.
  </Text>
</Box>
{/* Section 10 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    10. Service Availability and Uptime
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    10.1 Availability Goals
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We strive to maintain high availability and reliable access to the Platform. However, we do not 
    guarantee uninterrupted, error-free, or continuous service. The Platform is provided on an 
    "as available" basis.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    10.2 Scheduled Maintenance
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We may perform scheduled maintenance that temporarily limits or suspends access to the Platform. 
    Where practicable, we will provide advance notice of planned maintenance via email or Platform 
    notifications. Maintenance windows are typically scheduled during low-usage periods.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    10.3 Unplanned Outages
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Temporary outages may occur due to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Technical failures, hardware malfunctions, or software bugs</ListItem>
    <ListItem>Network connectivity issues or third-party service disruptions</ListItem>
    <ListItem>Security incidents requiring immediate response</ListItem>
    <ListItem>Excessive demand or resource constraints</ListItem>
    <ListItem>Updates, upgrades, or emergency patches</ListItem>
  </UnorderedList>
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  10.4 Force Majeure
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We are not liable for any failure or delay in performance due to circumstances beyond our 
    reasonable control, including but not limited to: natural disasters, acts of war or terrorism, 
    pandemics, government actions, power outages, internet infrastructure failures, cyberattacks, 
    or actions of third-party providers.
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    In the event of force majeure circumstances, we expressly disclaim all liability for any data 
    loss, corruption, or destruction of User Content and Simulation Output, regardless of cause. 
    This includes, but is not limited to, loss resulting from server failures, storage system 
    malfunctions, security breaches, or infrastructure damage occurring during such events.
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    You acknowledge that maintaining independent backups of all critical data is your sole 
    responsibility, and you waive any claims against us for data loss arising from force majeure events.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    10.5 Limitation of Liability for Service Interruptions
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    In accordance with Austrian law (Allgemeines Bürgerliches Gesetzbuch - ABGB):
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>We are not liable for damages resulting from temporary service unavailability, 
    except in cases of gross negligence (grobe Fahrlässigkeit) or willful misconduct (Vorsatz) 
    pursuant to §§ 1295 ff ABGB</ListItem>
    <ListItem>We are not liable for data loss during outages unless caused by our gross negligence 
    or intentional acts (§ 1324 ABGB)</ListItem>
    <ListItem>Our liability is limited to foreseeable, typically occurring damages; liability for 
    slight negligence (leichte Fahrlässigkeit) regarding purely financial losses is excluded to 
    the extent permitted by law</ListItem>
    <ListItem>These limitations do not apply to damages arising from injury to life, body, or health, 
    nor do they affect mandatory consumer protection rights under the Konsumentenschutzgesetz (KSchG)</ListItem>
  </UnorderedList>
  

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    10.6 No Compensation for Downtime
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Unless otherwise agreed in a separate Enterprise service level agreement (SLA), you are not 
    entitled to credits, refunds, or compensation for service interruptions or downtime. Compute 
    credits are not consumed during periods when the Platform is unavailable for processing.
  </Text>
</Box>

{/* Section 11 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    11. Fees, Payment, and Credits
  </Heading>
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  11.2 Pricing and Taxes
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  <Text as="span" fontWeight="semibold">Displayed Prices:</Text> All prices shown on the Platform 
  are net prices, exclusive of Value Added Tax (VAT) or other applicable taxes.
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  <Text as="span" fontWeight="semibold">VAT for EU Consumers (B2C):</Text> If you are an individual 
  consumer located in the European Union, VAT will be calculated and applied at checkout based on 
  your billing address, in accordance with EU VAT rules for digital services. The applicable VAT 
  rate is determined by your country of residence. Stripe, our payment processor, handles VAT 
  calculation and collection automatically.
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  <Text as="span" fontWeight="semibold">VAT for EU Business Customers (B2B):</Text> If you are a 
  business customer with a valid VAT identification number (VAT ID) registered in an EU member 
  state, the reverse charge mechanism may apply. To qualify for reverse charge:
</Text>
<UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
  <ListItem>You must provide your company name, address, and valid VAT ID during checkout</ListItem>
  <ListItem>Your VAT ID must be verifiable through the EU VIES (VAT Information Exchange System)</ListItem>
  <ListItem>A separate invoice will be issued showing the reverse charge notation</ListItem>
  <ListItem>You are responsible for self-assessing and reporting VAT in your jurisdiction</ListItem>
</UnorderedList>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  <Text as="span" fontWeight="semibold">Non-EU Customers:</Text> If you are located outside the 
  European Union, VAT generally does not apply. However, you are solely responsible for any import 
  duties, local taxes, or fees imposed by your jurisdiction.
</Text>
<Text color="gray.600" lineHeight="1.8">
  <Text as="span" fontWeight="semibold">Invoices:</Text> Invoices are generated automatically and 
  available through the Stripe Customer Portal accessible from your Settings page. For business 
  invoices requiring company details, please ensure your information is complete and accurate at 
  checkout.
</Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    11.3 Payment Processing
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Payments are processed securely through Stripe, Inc., a PCI-DSS compliant payment processor</ListItem>
    <ListItem>We accept major credit cards and other payment methods supported by Stripe</ListItem>
    <ListItem>Payment is due immediately upon purchase; credits are activated after successful payment confirmation</ListItem>
    <ListItem>We do not store your complete payment card details; this information is handled exclusively by Stripe</ListItem>
    <ListItem>You may manage your payment methods and billing history through the Stripe Customer Portal accessible from Settings</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    11.4 Failed Payments
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    If a payment fails, credits will not be activated. You may retry the payment or use an alternative 
    payment method. We are not responsible for fees charged by your bank or payment provider for 
    declined transactions.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  11.5 Credit Usage and Expiration
</Text>
<UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
  <ListItem>Credits are consumed based on actual GPU compute time used for simulations</ListItem>
  <ListItem>Processing time includes job initialization, computation, and data transfer overhead</ListItem>
  <ListItem>
    <Text as="span" fontWeight="semibold">Credit Expiration:</Text> Purchased credit hours are valid for 12 months from the date of purchase. After this period, unused credits will expire. This limitation complies with §6 KSchG and is necessary for operational planning and technical maintenance. You may contact us before expiry to request an extension in special cases.
  </ListItem>
  <ListItem>You will receive email notifications prior to credit expiration (typically 30 days and 7 days before expiry)</ListItem>
  {/* <ListItem>Credit balances and expiration dates are displayed in your Dashboard and Settings</ListItem> */}
  <ListItem>Credits are non-transferable between accounts and cannot be exchanged for cash</ListItem>
  <ListItem>Expired credits cannot be reinstated, extended, or refunded</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Important:</Text> By purchasing credits, you acknowledge 
    and accept the 12-month expiration period. We recommend using credits promptly to avoid forfeiture.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  11.6 Refunds and Cancellations
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Our refund and cancellation policies comply with EU and Austrian/German consumer protection laws:
  </Text>

  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>
      If you are a consumer residing in the EU, you have the right to withdraw from your purchase within 14 days under the EU Distance Selling Regulations (see Section 16 – Consumer Rights).
    </ListItem>
    <ListItem>
      Refunds for unused simulation credits may be requested during the withdrawal period, provided the service has not been partially or fully consumed.
    </ListItem>
    <ListItem>
      If you expressly request that the service begin during the 14-day withdrawal period, you acknowledge that your right of withdrawal is lost once the service has been fully performed or partially consumed. Any simulation hours already used are non-refundable.
    </ListItem>
    <ListItem>
      Subscriptions may be cancelled at any time and will remain active until the end of the current billing period.
    </ListItem>
    <ListItem>
      All refund and cancellation requests must be submitted through the contact form available under Account Settings.
    </ListItem>
  </UnorderedList>



  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    11.7 Promotion Codes and Discounts
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Promotional codes, discounts, or special offers are subject to their specific terms and conditions. 
    Promotions may not be combined unless explicitly stated. We reserve the right to modify or 
    discontinue promotions at any time. Abuse of promotional offers may result in account suspension.
  </Text>
</Box>

{/* Section 12 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    12. Data Storage, Retention, and Deletion
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.1 Storage of User Content
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We store your User Content (simulation files, code, inputs) and Simulation Output (results, 
    data files) on secure cloud infrastructure while your account is active. Storage is provided 
    at no additional cost for all pricing tiers, subject to reasonable use limits.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.2 Your Backup Responsibility
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    YOU ARE SOLELY RESPONSIBLE FOR MAINTAINING BACKUPS OF YOUR DATA.
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    While we implement reasonable security and redundancy measures, you should:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Download and store local copies of all important Simulation Output promptly</ListItem>
    <ListItem>Maintain your own backups of uploaded User Content</ListItem>
    <ListItem>Not rely solely on the Platform for long-term archival storage</ListItem>
    <ListItem>Export critical data before account deletion or extended inactivity</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.3 Data Deletion Triggers
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Your data may be deleted under the following circumstances:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">User-initiated deletion:</Text> When you delete 
    individual jobs via the Jobs page or request account deletion</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Account inactivity:</Text> Accounts inactive for 
    24 months or more may be subject to data cleanup after notice</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Account termination:</Text> Upon account closure 
    (voluntary or due to Terms violation), all associated data is deleted</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">GDPR deletion request:</Text> Upon valid exercise 
    of your right to erasure under Article 17 GDPR</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Legal requirement:</Text> If required by court 
    order or regulatory directive</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.4 Retention Periods
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">Simulation data:</Text> Retained while your 
    account is active; deleted upon account termination</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Account information:</Text> Deleted upon account 
    closure, subject to legal retention requirements</ListItem>      
    <ListItem><Text as="span" fontWeight="semibold">Billing records:</Text> Retained for seven (7) 
    years after the last transaction as required by Austrian tax and accounting law 
    (Bundesabgabenordnung - BAO, Unternehmensgesetzbuch - UGB)</ListItem>

    <ListItem><Text as="span" fontWeight="semibold">Security logs:</Text> May be retained for up 
    to 12 months for fraud prevention and security purposes</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Anonymized analytics:</Text> May be retained 
    indefinitely in aggregated, non-identifiable form</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.5 Deletion Process
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Upon deletion:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Data is marked for deletion and removed from active systems</ListItem>
    <ListItem>Backup copies may persist for up to 30 days before complete removal</ListItem>
    <ListItem>Deletion is permanent and irreversible; data cannot be recovered</ListItem>
    <ListItem>You will receive confirmation of account deletion via your registered email</ListItem>
  </UnorderedList>
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    12.6 No Liability for Data Loss
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We are not liable for data loss resulting from your failure to maintain backups, user-initiated 
    deletions, account inactivity, or circumstances described in Section 10 (Service Availability). 
    Liability for data loss due to our gross negligence or willful misconduct is governed by 
    Austrian law (§§ 1295 ff ABGB).
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    In the event of force majeure circumstances, we expressly disclaim all liability for any data 
    loss, corruption, or destruction of User Content and Simulation Output, regardless of cause. 
    This includes, but is not limited to, loss resulting from server failures, storage system 
    malfunctions, security breaches, or infrastructure damage occurring during such events.
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    You acknowledge that maintaining independent backups of all critical data is your sole 
    responsibility, and you waive any claims against us for data loss arising from force majeure 
    events or any other circumstances beyond our reasonable control.
  </Text>
</Box>
{/* Section 13 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    13. Intellectual Property Rights
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.1 Platform Ownership
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform, including all software, source code, algorithms, AI models, machine learning systems, 
    user interface designs, graphics, documentation, APIs, and all related intellectual property 
    ("Platform IP") is owned by Empirisch Tech GmbH and is protected by Austrian, EU, and 
    international copyright, trademark, patent, and trade secret laws.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.2 Trademarks and Branding
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    "SRS," "Scale-Resolving Simulation," associated logos, and all related names, marks, and slogans are
    trademarks or service marks of Empirisch Tech GmbH. You may not use these marks without our prior
    written consent, except as reasonably necessary to identify the Platform as the source of
    your Simulation Output.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.3 Limited License to Use Platform
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable 
    license to access and use the Platform solely for your internal business, research, or 
    educational purposes. This license does not include:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Any right to sublicense, resell, or redistribute access to the Platform</ListItem>
    <ListItem>Permission to use the Platform to provide services to third parties</ListItem>
    <ListItem>Any ownership interest in the Platform IP</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.4 Prohibited Activities
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You expressly agree NOT to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Copy, reproduce, modify, adapt, or create derivative works of the Platform or any 
    component thereof</ListItem>
    <ListItem>Reverse engineer, decompile, disassemble, or attempt to derive source code, algorithms, 
    or data structures from the Platform</ListItem>
    <ListItem>Remove, alter, or obscure any copyright notices, trademarks, or proprietary legends</ListItem>
    <ListItem>Use automated tools to scrape, extract, or copy Platform content or data</ListItem>
    <ListItem>Attempt to bypass, disable, or circumvent any security, licensing, or access control 
    measures</ListItem>
    <ListItem>Use the Platform to develop competing products or services</ListItem>
    <ListItem>Sublicense, rent, lease, or lend your access to third parties</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.5 Your Content
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    As stated in Section 7, you retain all intellectual property rights in your User Content and 
    Simulation Output. Nothing in these Terms transfers ownership of your proprietary data, models, 
    or results to us.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.6 Feedback and Suggestions
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    If you provide feedback, suggestions, ideas, or recommendations regarding the Platform 
    ("Feedback"), you grant us a perpetual, irrevocable, worldwide, royalty-free license to use, 
    modify, and incorporate such Feedback into the Platform without obligation or compensation 
    to you. You waive any moral rights in such Feedback.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    13.7 Copyright Infringement Claims
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    If you believe that content on the Platform infringes your copyright, please contact us via 
    the Settings contact form with: (a) identification of the copyrighted work, (b) identification 
    of the allegedly infringing material, (c) your contact information, and (d) a statement of 
    good faith belief that the use is unauthorized. We will respond in accordance with applicable 
    Austrian and EU law.
  </Text>
</Box>

{/* Section 14 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    14. Third-Party Software and Services
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    14.1 Simulation Tools
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform utilizes third-party simulation software, including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">PyFR:</Text> An
    open-source high-order Flux Reconstruction solver for scale-resolving simulation of industrial flow problems, released under a 3-Clause BSD License. Your use of PyFR-generated results is subject to the
    PyFR license terms.</ListItem>
    <ListItem>Additional solvers and libraries as may be added to the Platform</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You are responsible for complying with all applicable third-party license terms when using 
    Simulation Output for publication, commercial purposes, or redistribution.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    14.2 Third-Party Service Providers
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform integrates with third-party service providers including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">Stripe, Inc.:</Text> Payment processing and 
    billing management</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Cloudflare, Inc.:</Text> Security, bot protection 
    (Turnstile), and content delivery</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Cloud infrastructure providers:</Text> Server 
    hosting and GPU compute resources</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Your use of the Platform constitutes acceptance of these providers' terms of service and 
    privacy policies. We are not responsible for the actions, policies, or practices of third-party 
    providers.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    14.3 Open Source Components
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform may include open-source software components subject to their respective licenses 
    (e.g., MIT, BSD, Apache 2.0, GPL). A list of open-source components and their licenses is 
    available upon request. Nothing in these Terms restricts your rights under applicable 
    open-source licenses.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    14.4 No Warranty for Third-Party Components
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Third-party software and services are provided "AS IS" without warranty. We do not guarantee:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Accuracy or reliability of third-party simulation tools</ListItem>
    <ListItem>Continued availability of third-party services</ListItem>
    <ListItem>Compatibility with your specific use case</ListItem>
    <ListItem>That third-party components are free from defects or security vulnerabilities</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    14.5 Attribution and Citation Requirements
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Required Attribution:</Text> When publishing, presenting, 
    or distributing Simulation Output in the following contexts, you must provide attribution to
    SRS:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Academic papers, journal articles, theses, and dissertations</ListItem>
    <ListItem>Conference presentations and scientific posters</ListItem>
    <ListItem>Technical reports and white papers</ListItem>
    <ListItem>Commercial deliverables provided to clients or customers</ListItem>
    <ListItem>Marketing materials, case studies, or promotional content</ListItem>
    <ListItem>Any context where simulation methodology is described</ListItem>
  </UnorderedList>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Suggested Citation Format:</Text>
  </Text>
  <Text color="gray.500" lineHeight="1.8" mb={4} fontStyle="italic" pl={4} borderLeftWidth="3px" borderColor="brand.200">
    "Simulations performed using SRS (https://srs.numericalai.net)."
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Requested Attribution:</Text> For informal sharing on 
    social media platforms (LinkedIn, Twitter/X, etc.), we request but do not require that you 
    acknowledge SRS when sharing simulation results, visualizations, or insights generated
    using the Platform. A simple mention or tag is appreciated and helps support our community.
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Prohibited Misrepresentation:</Text> You may NOT:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Claim that SRS, Empirisch Tech GmbH endorses your work, conclusions, or organization</ListItem>
    <ListItem>Suggest official partnership or affiliation without written agreement</ListItem>
    <ListItem>Remove or obscure any attribution, watermarks, or metadata embedded in outputs</ListItem>
    <ListItem>Misrepresent the origin of simulation results</ListItem>
  </UnorderedList>
</Box>

{/* Section 15 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    15. Export Control and Restricted Use
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.1 Export Control Regulations
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    The Platform, including simulation software, AI systems, and generated results, may be subject 
    to export control laws and regulations, including:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>EU Dual-Use Regulation (EU) 2021/821</ListItem>
    <ListItem>Austrian Foreign Trade Act (Außenwirtschaftsgesetz - AußWG)</ListItem>
    <ListItem>U.S. Export Administration Regulations (EAR), where applicable</ListItem>
    <ListItem>International Traffic in Arms Regulations (ITAR), where applicable</ListItem>
    <ListItem>EU and UN sanctions regimes</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.2 Prohibited Applications
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You expressly agree NOT to use the Platform, its AI features, or any Simulation Output for:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem><Text as="span" fontWeight="semibold">Weapons development:</Text> Design, development, 
    production, or testing of nuclear, chemical, biological, or conventional weapons, missiles, 
    or delivery systems</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Military applications:</Text> Defense systems, 
    military vehicles, ordnance, or other military end-uses without proper authorization</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Nuclear activities:</Text> Nuclear weapons 
    programs, uranium enrichment, plutonium processing, or unsafeguarded nuclear facilities</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Dual-use technologies:</Text> Applications listed 
    in Annex I of EU Regulation 2021/821 without appropriate export licenses</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Surveillance systems:</Text> Mass surveillance, 
    human rights violations, or repression technologies</ListItem>
    <ListItem><Text as="span" fontWeight="semibold">Sanctioned parties:</Text> Any activities 
    involving sanctioned individuals, entities, or countries</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.3 Sanctioned Countries and Parties
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You may not access the Platform from, or transfer any results or technology to, countries 
    subject to comprehensive sanctions, including but not limited to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>North Korea (DPRK)</ListItem>
    <ListItem>Iran</ListItem>
    <ListItem>Syria</ListItem>
    <ListItem>Cuba</ListItem>
    <ListItem>Crimea, Donetsk, and Luhansk regions</ListItem>
    <ListItem>Any other countries or regions subject to EU, UN, or applicable sanctions</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You must not provide access to, or share results with, any individual or entity listed on 
    EU, UN, or other applicable sanctions or denied party lists.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.4 User Representations and Warranties
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    By using the Platform, you represent and warrant that:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>You are not located in, or a national or resident of, any sanctioned country</ListItem>
    <ListItem>You are not listed on any sanctions or denied party list</ListItem>
    <ListItem>You will not use the Platform for any prohibited end-use</ListItem>
    <ListItem>You will obtain all required export licenses or authorizations before any controlled transfer</ListItem>
    <ListItem>You will notify us immediately if you become aware of any potential export control violation</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.5 Consequences of Violation
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Violation of export control laws or this Section 15 may result in:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Immediate account suspension or termination without notice</ListItem>
    <ListItem>Forfeiture of all credits and data without refund</ListItem>
    <ListItem>Reporting to relevant authorities (Austrian, EU, or international)</ListItem>
    <ListItem>Civil and criminal penalties under applicable law</ListItem>
    <ListItem>Liability for all damages, fines, and legal costs incurred by us</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    15.6 Our Right to Restrict Access
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    We reserve the right to screen users and transactions, restrict access from certain jurisdictions, 
    require additional documentation, and refuse service to any user we reasonably believe may 
    violate export control laws, in our sole discretion and without liability.
  </Text>
</Box>
{/* Section 16 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    16. EU Consumer Rights
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    16.1 Right of Withdrawal (Widerrufsrecht)
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    Under the EU Consumer Rights Directive (2011/83/EU) and Austrian FAGG (Fern- und 
    Auswärtsgeschäfte-Gesetz), you have the right to withdraw from this contract within 
    fourteen (14) days without giving any reason. The withdrawal period expires 14 days 
    from the day of contract conclusion (i.e., date of purchase).
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    To exercise your right of withdrawal, you must inform us of your decision by a clear 
    statement using the contact form available under Account Settings.
  </Text>


  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    16.2 Exception for Digital Services
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    As stated in Section 11.6, your right of withdrawal expires once you have:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Explicitly consented to immediate performance of the service before the 
    withdrawal period expires, AND</ListItem>
    <ListItem>Acknowledged that you will lose your right of withdrawal once the service 
    is fully performed</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    For simulation credits, this means once you have consumed credits by running simulations, 
    those consumed credits are non-refundable. Unused credits remain subject to the 14-day 
    withdrawal right.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    16.3 Effects of Withdrawal
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    If you withdraw from this contract, we shall reimburse all payments received from you, 
    minus the value of any services already consumed, without undue delay and no later than 
    fourteen (14) days from the day we receive your withdrawal notice. We will use the same 
    payment method you used for the original transaction unless you have expressly agreed 
    otherwise.
  </Text>
</Box>

{/* Section 17 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    17. Privacy, Data Protection & GDPR Compliance
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    17.1 Legal Framework
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We process personal data in accordance with:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>EU General Data Protection Regulation (GDPR - Regulation 2016/679)</ListItem>
    <ListItem>Austrian Data Protection Act (Datenschutzgesetz - DSG)</ListItem>
    <ListItem>EU ePrivacy Directive (2002/58/EC) regarding cookies and electronic communications</ListItem>
    <ListItem>Our Privacy Policy, available <Link as={RouterLink} fontWeight="medium" to="/privacy" color="brand.500">here</Link></ListItem>
  </UnorderedList>
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  17.2 Consent for AI Model Training
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  We offer an optional setting that allows you to consent to the use of your anonymized 
  simulation data for improving our AI models. This consent is:
</Text>
<UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
  <ListItem><Text as="span" fontWeight="semibold">Voluntary:</Text> Disabled by default; 
  you must explicitly opt-in via Account Settings</ListItem>
  <ListItem><Text as="span" fontWeight="semibold">Revocable:</Text> You can withdraw consent 
  at any time by disabling the setting; future data will no longer be used for training</ListItem>
  <ListItem><Text as="span" fontWeight="semibold">Anonymized:</Text> Only anonymized and 
  aggregated data is used; no personally identifiable information is included in training datasets</ListItem>
  <ListItem><Text as="span" fontWeight="semibold">Separate from service:</Text> Your choice 
  does not affect your access to any Platform features</ListItem>
</UnorderedList>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  Legal basis: Article 6(1)(a) GDPR - Consent
</Text>


  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    17.8 Exercising Your Rights
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    To exercise any of your data protection rights:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Use the contact form in your Settings page</ListItem>
    <ListItem>For account deletion: Use the "Delete Account" feature in Settings</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8">
    We will respond to your request within 30 days, as required by GDPR. We may request 
    identity verification before processing your request. For full details on how we handle 
    your data, please review our <Link as={RouterLink} to="/privacy" color="brand.500" 
    fontWeight="medium">Privacy Policy</Link>.
  </Text>
</Box>
{/* Section 18 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    18. Limitation of Liability
  </Heading>
  
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EMPIRISCH TECH GMBH AND ITS OFFICERS, DIRECTORS, 
    EMPLOYEES, AGENTS, AND LICENSORS DISCLAIM ALL LIABILITY FOR ANY DAMAGES, CLAIMS, OR LOSSES 
    ARISING FROM OR RELATED TO YOUR USE OF THE PLATFORM, AI FEATURES, SIMULATION OUTPUT, OR 
    ANY SERVICES PROVIDED.
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    This includes, without limitation:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Any direct, indirect, incidental, special, consequential, or punitive damages</ListItem>
    <ListItem>Loss of profits, revenue, data, goodwill, or business opportunities</ListItem>
    <ListItem>Incorrect, incomplete, or misleading AI outputs or simulation results</ListItem>
    <ListItem>Any decisions, designs, or actions based on Platform outputs</ListItem>
    <ListItem>Service interruptions, data loss, or security breaches</ListItem>
    <ListItem>Third-party claims arising from your use of outputs</ListItem>
  </UnorderedList>

  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    YOU EXPRESSLY ACKNOWLEDGE THAT YOUR USE OF THE PLATFORM IS ENTIRELY AT YOUR OWN RISK. 
    THE PLATFORM, AI FEATURES, AND SIMULATION OUTPUT ARE PROVIDED "AS IS" AND "AS AVAILABLE" 
    WITHOUT ANY WARRANTIES OF ANY KIND.
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    In any event, our total aggregate liability shall not exceed EUR 100 or the amount you paid 
    us in the three (3) months preceding the claim, whichever is less.
  </Text>

  <Text color="gray.600" lineHeight="1.8">
    You agree to fully indemnify and hold harmless Empirisch Tech GmbH from any claims, damages, and 
    expenses arising from your use of the Platform or any outputs generated.
  </Text>
</Box>
{/* Section 19 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    19. Indemnification
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    19.1 Your Indemnification Obligations
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    You agree to indemnify, defend, and hold harmless Empirisch Tech GmbH, its parent companies, 
    subsidiaries, affiliates, officers, directors, employees, agents, licensors, and service 
    providers from and against any and all claims, damages, losses, liabilities, costs, and 
    expenses (including reasonable attorneys' fees and court costs) arising out of or related to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Your access to or use of the Platform, AI features, or Simulation Output</ListItem>
    <ListItem>Your User Content, including any claims of intellectual property infringement</ListItem>
    <ListItem>Your violation of these Terms of Service or any applicable laws or regulations</ListItem>
    <ListItem>Your violation of export control laws, sanctions, or restricted use provisions</ListItem>
    <ListItem>Your reliance on or implementation of AI-generated outputs without proper verification</ListItem>
    <ListItem>Your use of AI outputs or Simulation Output in regulated, high-risk, or 
    safety-critical applications</ListItem>
    <ListItem>Any products, services, or systems you develop using Simulation Output</ListItem>
    <ListItem>Any third-party claims resulting from your actions or omissions</ListItem>
    <ListItem>Your breach of any representations or warranties made in these Terms</ListItem>
    <ListItem>Any unauthorized access to or use of the Platform through your account</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    19.2 Defense and Control
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We reserve the right, at our own expense, to assume the exclusive defense and control of 
    any matter subject to indemnification by you. In such case, you agree to cooperate with 
    our defense of such claim and provide all reasonably requested assistance at your expense.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    19.3 Notification
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We will provide you with prompt written notice of any claim for which we seek indemnification. 
    However, failure to provide timely notice shall not relieve you of your indemnification 
    obligations except to the extent you are materially prejudiced by such delay.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    19.4 Survival
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Your indemnification obligations under this Section shall survive termination or expiration 
    of these Terms and your use of the Platform.
  </Text>
</Box>

{/* Section 20 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    20. Modifications to Terms and Services
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    20.1 Right to Modify Terms
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We reserve the right to modify, update, or replace these Terms of Service at any time at 
    our sole discretion. Changes may be made for any reason, including but not limited to:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Legal or regulatory compliance requirements</ListItem>
    <ListItem>Changes in applicable laws (EU AI Act, GDPR, export controls, etc.)</ListItem>
    <ListItem>Security or operational improvements</ListItem>
    <ListItem>New features, services, or pricing changes</ListItem>
    <ListItem>Clarification of existing provisions</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    20.2 Notification of Changes
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    For material changes, we will provide notice by:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Posting the updated Terms on the Platform with a new "Last Updated" date</ListItem>
    <ListItem>Sending an email notification to your registered email address</ListItem>
    <ListItem>Displaying a prominent notice upon login</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    For non-material changes, posting updated Terms on the Platform shall constitute sufficient 
    notice. It is your responsibility to review the Terms periodically.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    20.3 Acceptance of Changes
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    YOUR CONTINUED USE OF THE PLATFORM AFTER THE EFFECTIVE DATE OF ANY CHANGES CONSTITUTES 
    YOUR ACCEPTANCE OF THE MODIFIED TERMS. IF YOU DO NOT AGREE TO THE UPDATED TERMS, YOU MUST 
    STOP USING THE PLATFORM IMMEDIATELY.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    20.4 Modifications to Platform and AI Features
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We may at any time, without notice or liability:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>Modify, update, or discontinue any Platform features, tools, or services</ListItem>
    <ListItem>Update, retrain, or replace AI models and algorithms</ListItem>
    <ListItem>Change AI output formats, capabilities, or behavior</ListItem>
    <ListItem>Adjust validation checks, security measures, or processing pipelines</ListItem>
    <ListItem>Modify pricing, credit packages, or subscription tiers</ListItem>
    <ListItem>Impose usage limits, rate limits, or access restrictions</ListItem>
  </UnorderedList>
  <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
    Changes to AI models may affect the behavior, accuracy, or consistency of AI outputs. 
    You should not rely on AI outputs remaining consistent across Platform updates.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    20.5 No Obligation to Maintain
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    We have no obligation to maintain, support, or update any feature of the Platform. 
    We may suspend or terminate the Platform entirely at our discretion, subject to 
    providing reasonable notice where practicable and honoring any prepaid credits as 
    required by applicable consumer protection laws.
  </Text>
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
  20.6 Notifications – Best Effort Basis
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  All notifications generated by the Platform, including but not limited to:
</Text>
<UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
  <ListItem>Credit expiration warnings and reminders</ListItem>
  <ListItem>Account status updates and security alerts</ListItem>
  <ListItem>Planned maintenance announcements</ListItem>
  <ListItem>Terms of Service or Privacy Policy updates</ListItem>
  <ListItem>Simulation completion or failure notifications</ListItem>
  <ListItem>Billing confirmations and payment receipts</ListItem>
  <ListItem>Newsletter and promotional communications</ListItem>
</UnorderedList>
<Text color="gray.600" lineHeight="1.8" mb={4}>
  are provided on a <Text as="span" fontWeight="semibold">"best effort" basis only</Text>. 
  We do not guarantee the delivery, timing, or receipt of any notification. Notifications 
  may be delayed, filtered by spam systems, blocked by email providers, or fail to reach 
  you for reasons beyond our control.
</Text>
<Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="semibold">
  YOU ARE SOLELY RESPONSIBLE FOR:
</Text>
<UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
  <ListItem>Maintaining a current, valid email address in your account settings</ListItem>
  <ListItem>Configuring your email settings to receive messages from our domain</ListItem>
  <ListItem>Regularly checking your account dashboard for important updates</ListItem>
  <ListItem>Monitoring your credit balance and expiration dates directly in the Platform</ListItem>
  <ListItem>Reviewing the Platform periodically for Terms or service changes</ListItem>
</UnorderedList>
<Text color="gray.600" lineHeight="1.8">
  Failure to receive a notification does not relieve you of any obligation under these Terms, 
  does not extend any deadlines (including credit expiration), and does not entitle you to 
  any refund, credit, or compensation. We expressly disclaim all liability for any losses, 
  damages, or missed deadlines resulting from undelivered or delayed notifications.
</Text>  
</Box>
{/* Section 21 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    21. Governing Law, Jurisdiction & Dispute Resolution
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    21.1 Governing Law
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    These Terms and any disputes arising out of or relating to them or the Platform shall be 
    governed by and construed in accordance with the laws of the Republic of Austria, without 
    regard to its conflict of laws principles. The United Nations Convention on Contracts for 
    the International Sale of Goods (CISG) is expressly excluded.
  </Text>

  <Text color="gray.600" lineHeight="1.8" mb={4}>
    If you are a business, entrepreneur, or professional (Unternehmer within the meaning of 
    § 1 KSchG), you agree that:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>The exclusive place of jurisdiction for all disputes shall be the competent 
    courts of Vienna, Austria</ListItem>
    <ListItem>We retain the right to bring proceedings in any other court of competent 
    jurisdiction</ListItem>
    <ListItem>You waive any objection to venue or inconvenient forum in Vienna</ListItem>
  </UnorderedList>


  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    21.2 Class Action Waiver
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    To the maximum extent permitted by law, you agree that any dispute resolution proceedings 
    will be conducted only on an individual basis and not in a class, consolidated, or 
    representative action. If this waiver is found unenforceable, the remainder of this 
    Section remains in effect.
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    21.3 Time Limitation
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    Any claim or cause of action arising from or relating to these Terms or the Platform 
    must be filed within one (1) year after such claim arose, or it shall be permanently 
    barred. This limitation does not apply where prohibited by applicable law.
  </Text>
</Box>

{/* Section 22 */}
<Box>
  <Heading size="lg" color="gray.800" mb={4}>
    22. Contact Information
  </Heading>
  
  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    22.1 Company Details
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    <Text as="span" fontWeight="semibold">Empirisch Tech GmbH</Text><br />
    Rennweg 88<br />
    1030 Vienna, Austria<br /><br />
    Commercial Register: FN 566531 i<br />
    Commercial Court: Handelsgericht Wien<br />
    VAT ID: ATU 776 77 257
  </Text>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    22.2 Contact Channels
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>
      <Text as="span" fontWeight="semibold">Please use the contact form under Settings when logged in for all inquiries</Text>
    </ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    22.3 Response Times
  </Text>
  <Text color="gray.600" lineHeight="1.8" mb={4}>
    We aim to respond to inquiries within:
  </Text>
  <UnorderedList spacing={2} pl={4} color="gray.600" mb={4}>
    <ListItem>General inquiries: 3 business days</ListItem>
    <ListItem>Technical support: 72 hours on business days</ListItem>
    <ListItem>Data protection requests: 30 days (as required by GDPR)</ListItem>
    <ListItem>Legal notices: 14 business days</ListItem>
  </UnorderedList>

  <Text color="gray.700" lineHeight="1.8" mb={3} fontWeight="bold">
    22.4 Language
  </Text>
  <Text color="gray.600" lineHeight="1.8">
    These Terms are provided in English. In case of any translation, the English version 
    shall prevail. Support is available in English and German.
  </Text>
</Box>
          </VStack>

          <Divider />

          <VStack spacing={4} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              By using SRS's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
            </Text>
          </VStack>
        </VStack>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default TermsOfService;
